import React, { useCallback, useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { useAuth0 } from "@auth0/auth0-react"
import {
  Container, Row, Col, Card, CardBody, CardTitle, Table, Badge, Spinner,
  Label, Button, Alert,
} from "reactstrap"
import Select from "react-select"
import { withTranslation } from "react-i18next"

import { Permissions } from "../../constants/enums"
import {
  getServiceValidationAudit,
  getServiceValidationDiff,
} from "../../helpers/api_rombo_helper"
import { fieldLabel, formatFieldValue } from "./auditFormat"

// Vista de comparación (diff) de dos snapshots del servicio, lado a lado (SV2-T11).
// - Selector de versiones (desde/hasta); default baseline (0) <-> última.
// - Consume GET /{code}/diff?from=&to= (T7) -> { diff: { added, removed, changed } }.
// - Resalta add/removed/changed y colapsa lo que no cambió.
const asRows = (diff) => {
  // Normaliza el diff del backend a filas uniformes para la tabla.
  // Formatos tolerados:
  //   changed: { campo: {old, new} }  o  { campo: [old, new] }
  //   added:   { campo: valor }       removed: { campo: valor }
  const rows = []
  const changed = diff?.changed || {}
  const added = diff?.added || {}
  const removed = diff?.removed || {}

  Object.entries(changed).forEach(([field, val]) => {
    const old = Array.isArray(val) ? val[0] : val?.old
    const nw = Array.isArray(val) ? val[1] : val?.new
    rows.push({ field, kind: "changed", old, new: nw })
  })
  Object.entries(added).forEach(([field, val]) => {
    rows.push({ field, kind: "added", old: undefined, new: val })
  })
  Object.entries(removed).forEach(([field, val]) => {
    rows.push({ field, kind: "removed", old: val, new: undefined })
  })
  return rows.sort((a, b) => a.field.localeCompare(b.field))
}

const kindColor = (k) =>
  k === "added" ? "success" : k === "removed" ? "danger" : "warning"

const ServiceValidationDiff = ({ t }) => {
  const { code } = useParams()
  const { getAccessTokenSilently, user } = useAuth0()
  const currentSub = user?.sub || null
  const [infoUser, setInfoUser] = useState({ permissions: [] })
  const [versions, setVersions] = useState([])
  const [from, setFrom] = useState(0)
  const [to, setTo] = useState(1)
  const [diff, setDiff] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({ data: Login.data, loading: Login.loading }),
  )
  const { data, loading } = useSelector(LoginProperties)

  useEffect(() => {
    if (data?.data) setInfoUser(data.data)
  }, [data, loading])

  const canRead = infoUser.permissions?.includes(Permissions.SERVICE_VALIDATION_READ)

  // Resuelve sub -> nombre legible: para el usuario actual usa su full_name
  // (de /api/v1/admin/me, en infoUser); otros subs quedan como están (los
  // snapshots históricos solo guardan el sub).
  const resolveUserName = (sub) => {
    if (sub && currentSub && sub === currentSub && infoUser?.full_name) {
      return infoUser.full_name
    }
    return sub
  }

  const authHeader = useCallback(async () => {
    const accessToken = await getAccessTokenSilently()
    return { Authorization: `Bearer ${accessToken}` }
  }, [getAccessTokenSilently])

  // Carga las versiones disponibles (para poblar los selectores) y precarga
  // un default útil: baseline (menor) vs última (mayor).
  useEffect(() => {
    const loadVersions = async () => {
      if (!canRead) return
      try {
        const token = await authHeader()
        const a = await getServiceValidationAudit(token, code)
        const vs = (a?.versions || []).map((v) => v.version_no).sort((x, y) => x - y)
        setVersions(vs)
        if (vs.length >= 2) {
          setFrom(vs[0])
          setTo(vs[vs.length - 1])
        } else if (vs.length === 1) {
          setFrom(vs[0]); setTo(vs[0])
        }
      } catch (_e) {
        setVersions([])
      }
    }
    loadVersions()
  }, [authHeader, canRead, code])

  const loadDiff = useCallback(async () => {
    if (!canRead) return
    setLoading(true); setError(null)
    try {
      const token = await authHeader()
      const resp = await getServiceValidationDiff(token, code, { from, to })
      setDiff(resp?.diff || resp || {})
    } catch (e) {
      setError(e?.response?.data?.detail || t("LoadError"))
      setDiff(null)
    } finally {
      setLoading(false)
    }
  }, [authHeader, canRead, code, from, to, t])

  const rows = diff ? asRows(diff) : []

  const fromOptions = versions.length === 0
    ? [{ label: "0", value: 0 }]
    : versions.map((v) => ({ label: String(v), value: v }))
  const toOptions = versions.length === 0
    ? [{ label: "1", value: 1 }]
    : versions.map((v) => ({ label: `${v}${v === 0 ? " (base)" : ""}`, value: v }))

  if (!canRead) {
    return (
      <div className="page-content">
        <Container fluid>
          <span className="error-message-requests">
            <i className="fa fa-exclamation-circle" /> {t("NoPermissionsViewServices")}
          </span>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col xs={12} className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{t("SnapshotDiff") || "Diff"}: {code}</h4>
            <Link to={`/service-validation/${code}`} className="btn btn-sm btn-light">
              {t("Detail")}
            </Link>
          </Col>
        </Row>

        {error && <Alert color="danger">{error}</Alert>}

        <Card className="mb-3">
          <CardBody>
            <Row className="sv-filters g-2 align-items-end">
              <Col xs={6} md={3}>
                <Label className="form-label mb-1">{t("From") || "Desde"} (v)</Label>
                <Select
                  classNamePrefix="select2-selection"
                  className="sv-filter-select"
                  isSearchable={false}
                  options={fromOptions}
                  value={fromOptions.find((o) => o.value === from) || null}
                  onChange={(opt) => setFrom(Number(opt?.value ?? 0))}
                />
              </Col>
              <Col xs={6} md={3}>
                <Label className="form-label mb-1">{t("To") || "Hasta"} (v)</Label>
                <Select
                  classNamePrefix="select2-selection"
                  className="sv-filter-select"
                  isSearchable={false}
                  options={toOptions}
                  value={toOptions.find((o) => o.value === to) || null}
                  onChange={(opt) => setTo(Number(opt?.value ?? 0))}
                />
              </Col>
              <Col xs={12} md={3} className="d-flex align-items-end">
                <Button color="primary" className="sv-search-btn" onClick={loadDiff} disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : <><i className="bx bx-git-compare me-1" />{t("Compare") || "Comparar"}</>}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CardTitle tag="h5" className="d-flex justify-content-between">
              <span>{t("Changes") || "Cambios"}</span>
              {diff && (
                <span className="text-muted small align-self-center">
                  v{from} → v{to} · {rows.length} {t("Changes") || "cambios"}
                </span>
              )}
            </CardTitle>

            {isLoading ? (
              <div className="text-center py-4"><Spinner color="primary" /></div>
            ) : !diff ? (
              <p className="text-muted mb-0">{t("SelectVersionsToCompare") || "Selecciona versiones y compara."}</p>
            ) : rows.length === 0 ? (
              <Alert color="info" className="mb-0">{t("NoDifferences") || "Sin diferencias entre estas versiones."}</Alert>
            ) : (
              <Table className="table table-bordered mb-0" size="sm">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>{t("Type") || "Tipo"}</th>
                    <th>{t("Field") || "Campo"}</th>
                    <th>v{from}</th>
                    <th>v{to}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.kind}-${r.field}`}>
                      <td><Badge color={kindColor(r.kind)}>{r.kind}</Badge></td>
                      <td className="fw-medium">{fieldLabel(r.field)}</td>
                      <td className={r.kind === "removed" ? "text-danger" : ""}>
                        {formatFieldValue(r.field, r.old, t, resolveUserName)}
                      </td>
                      <td className={r.kind === "added" ? "text-success" : r.kind === "changed" ? "fw-bold" : ""}>
                        {formatFieldValue(r.field, r.new, t, resolveUserName)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default withTranslation()(ServiceValidationDiff)
